import { Controller } from "react-hook-form"
import { useLoginForm } from "./model"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupError,
  InputGroupInput,
} from "@/components/ui/input-group"
import { AlertCircleIcon, EyeIcon, EyeOffIcon, Mail } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Alert, AlertTitle } from "@/components/ui/alert"
import { useMemo, useState } from "react"
import { Lock } from "lucide-react"
import { DISCOVERED_TRANSLATION_KEYS, LANG_KEYS } from "@/shared/constants"
import { useAutoTranslation } from "@/entities"
import { SvgApple, SvgGoogle } from "@/shared/ui/svg"
import { useTheme } from "@/components/theme-provider"
import { useAppleAuth } from "../use-apple-auth"
import { useGoogleAuth } from "../use-google-auth"

interface Props {
  onForgotPassClick: () => void
}

export const LoginForm = ({ onForgotPassClick }: Props) => {
  const { t } = useAutoTranslation()
  const {
    control,
    handleSubmit,
    errors,
    formError,
    setFormError,
    isLoading,
    continueAsGuest,
    isGuestLoading,
  } = useLoginForm()
  const [showPassword, setShowPassword] = useState(false)
  const { theme } = useTheme()
  const { signInWithGoogle, isLoading: isGoogleLoading } = useGoogleAuth()
  const { signInWithApple, isLoading: isAppleLoading } = useAppleAuth()
  const appleLogoVariant = useMemo(() => {
    return theme === "dark" ? "white" : "black"
  }, [theme])

  return (
    <form
      className="relative flex h-full flex-col"
      onSubmit={handleSubmit}
    >
      {/* Social Login First - Reduces friction (Slack/Notion pattern) */}
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={signInWithGoogle}
          isLoading={isGoogleLoading}
          className="h-11 gap-3 font-medium"
        >
          <SvgGoogle className="size-5" />
          {t(LANG_KEYS.CONTINUE_WITH_GOOGLE)}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={signInWithApple}
          isLoading={isAppleLoading}
          className="h-11 gap-3 font-medium"
        >
          <SvgApple variant={appleLogoVariant} className="size-5" />
          {t(LANG_KEYS.CONTINUE_WITH_APPLE)}
        </Button>
      </div>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card text-muted-foreground px-3">
            {t(LANG_KEYS.OR_CONTINUE_WITH_EMAIL)}
          </span>
        </div>
      </div>

      {/* Email Form */}
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col gap-5">
          <Controller
            control={control}
            name="email"
            rules={{
              required: {
                value: true,
                message: t(LANG_KEYS.REQUIRED),
              },
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: t(LANG_KEYS.INVALID_EMAIL_FORMAT),
              },
            }}
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                <Label htmlFor="login-email">{t(LANG_KEYS.EMAIL_ADDRESS)}</Label>
                <InputGroup>
                  <InputGroupInput
                    id="login-email"
                    type="email"
                    autoFocus
                    autoComplete="email"
                    placeholder="name@example.com"
                    value={field.value}
                    onChange={field.onChange}
                    aria-invalid={!!errors.email}
                  />
                  <InputGroupAddon>
                    <Mail />
                  </InputGroupAddon>
                  <InputGroupError>{errors.email?.message}</InputGroupError>
                </InputGroup>
              </div>
            )}
          />

          <Controller
            control={control}
            name="password"
            rules={{
              required: {
                value: true,
                message: t(LANG_KEYS.REQUIRED),
              },
              minLength: {
                value: 6,
                message: t(LANG_KEYS.MIN_PASS_LENGTH),
              },
            }}
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password">{t(LANG_KEYS.PASSWORD)}</Label>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-xs font-normal"
                    onClick={onForgotPassClick}
                  >
                    {t(LANG_KEYS.FORGOT_PASSWORD)}
                  </Button>
                </div>
                <InputGroup>
                  <InputGroupInput
                    type={showPassword ? "text" : "password"}
                    id="login-password"
                    autoComplete="current-password"
                    value={field.value}
                    onChange={field.onChange}
                    aria-invalid={!!errors.password}
                  />
                  <InputGroupAddon align="inline-end">
                    <Button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      size="icon"
                      className="size-7 hover:bg-transparent"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeIcon className="size-4" /> : <EyeOffIcon className="size-4" />}
                    </Button>
                  </InputGroupAddon>
                  <InputGroupAddon>
                    <Lock />
                  </InputGroupAddon>
                  <InputGroupError>{errors.password?.message}</InputGroupError>
                </InputGroup>
              </div>
            )}
          />

          {formError && (
            <Alert
              key={formError}
              variant="destructive"
              onClose={() => setFormError("")}
            >
              <AlertCircleIcon className="size-4" />
              <AlertTitle>{formError}</AlertTitle>
            </Alert>
          )}
        </div>

        <Button type="submit" isLoading={isLoading} className="mt-6 h-11 font-medium">
          {t(LANG_KEYS.SIGN_IN)}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={continueAsGuest}
          isLoading={isGuestLoading}
          className="mt-2 h-11 font-medium"
        >
          {t(DISCOVERED_TRANSLATION_KEYS.CONTINUE_AS_GUEST)}
        </Button>
        <p className="text-muted-foreground text-center text-xs">
          {t(DISCOVERED_TRANSLATION_KEYS.GUEST_MODE_DESCRIPTION)}
        </p>
      </div>
    </form>
  )
}
